import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../store/AuthContext';
import { ClipboardList, Plus, User, Sparkles, X } from 'lucide-react';

interface CareNote { id: string; resident_id: string; resident_name: string; room_number: string | null; author_name: string; note: string; shift: string; note_type: string; created_at: string; }
interface Resident { id: string; full_name: string; room_number: string | null; }

export function CareNotesPage() {
  const { user } = useAuth();
  const [notes, setNotes] = useState<CareNote[]>([]);
  const [residents, setResidents] = useState<Resident[]>([]);
  const [loading, setLoading] = useState(true);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedResident, setSelectedResident] = useState('');
  const [noteText, setNoteText] = useState('');
  const [shift, setShift] = useState('day');
  const [authorName, setAuthorName] = useState('');
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [shiftFilter, setShiftFilter] = useState('all');

  useEffect(() => { loadData(); }, [user]);

  const loadData = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data: profile } = await supabase.from('profiles').select('tenant_id, full_name').eq('id', user.id).single();
      if (!profile?.tenant_id) { setLoading(false); return; }
      setTenantId(profile.tenant_id);
      if (profile.full_name) setAuthorName(profile.full_name);
      const { data: resData } = await supabase.from('residents').select('id, full_name, room_number').eq('tenant_id', profile.tenant_id).eq('status', 'active').order('full_name');
      if (resData) setResidents(resData);
      const { data: notesData } = await supabase.from('care_notes').select('id, resident_id, author_name, note, shift, created_at, residents!care_notes_resident_id_fkey(full_name, room_number)').eq('tenant_id', profile.tenant_id).order('created_at', { ascending: false }).limit(50);
      if (notesData) {
        setNotes(notesData.map((n: any) => ({
          id: n.id, resident_id: n.resident_id, resident_name: n.residents?.full_name || 'Unknown',
          room_number: n.residents?.room_number || null, author_name: n.author_name,
          note: n.note, shift: n.shift, note_type: 'Shift Note', created_at: n.created_at
        })));
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const saveNote = async () => {
    if (!noteText.trim() || !selectedResident) { setError('Please select a resident and enter a note'); return; }
    setSaving(true); setError('');
    try {
      const { error: err } = await supabase.from('care_notes').insert({ tenant_id: tenantId, resident_id: selectedResident, author_name: authorName || 'Staff', author_id: user?.id, note: noteText.trim(), shift });
      if (err) throw err;
      setNoteText(''); setSelectedResident(''); setShift('day'); setShowForm(false); await loadData();
    } catch (err: any) { setError(err.message || 'Something went wrong'); }
    finally { setSaving(false); }
  };

  const generateAiNote = async () => {
    if (!selectedResident) { setError('Please select a resident first'); return; }
    setGenerating(true); setError('');
    try {
      const resident = residents.find(r => r.id === selectedResident);
      const response = await fetch('/api/ai/generate-note', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ residentName: resident?.full_name || 'Resident', shift, recentActivity: { shift, timestamp: new Date().toISOString() } }) });
      const data = await response.json();
      if (data.note) { setNoteText(data.note); } else { setError(data.error || 'Failed to generate note'); }
    } catch { setError('AI generation failed. Try again.'); }
    finally { setGenerating(false); }
  };

  const filtered = shiftFilter === 'all' ? notes : notes.filter(n => n.shift === shiftFilter);
  const today = new Date(); today.setHours(0,0,0,0);
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  const groupByDay = (list: CareNote[]) => {
    const groups: Record<string, CareNote[]> = {};
    list.forEach(n => {
      const d = new Date(n.created_at); d.setHours(0,0,0,0);
      const label = d.getTime() === today.getTime() ? 'Today' : d.getTime() === yesterday.getTime() ? 'Yesterday' : d.toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric' });
      if (!groups[label]) groups[label] = [];
      groups[label].push(n);
    });
    return groups;
  };
  const grouped = groupByDay(filtered);
  const formatTime = (d: string) => new Date(d).toLocaleString('en-CA', { hour: '2-digit', minute: '2-digit' });
  const dayCount = notes.filter(n => n.shift === 'day' && new Date(n.created_at).toDateString() === new Date().toDateString()).length;
  const nightCount = notes.filter(n => n.shift === 'night' && new Date(n.created_at).toDateString() === new Date().toDateString()).length;
  const eveningCount = notes.filter(n => n.shift === 'evening' && new Date(n.created_at).toDateString() === new Date().toDateString()).length;
  const todayCount = notes.filter(n => new Date(n.created_at).toDateString() === new Date().toDateString()).length;

  if (loading) return (
    <div className="flex items-center justify-center h-[60vh] text-[rgb(var(--admin-text-muted))] text-base">Loading care notes...</div>
  );

  return (
    <main className="flex-1 p-6 space-y-6 overflow-auto">

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-semibold text-[rgb(var(--admin-text-primary))]">Shift Handoff Notes</h1>
          <p className="text-sm text-[rgb(var(--admin-text-muted))]">{notes.length} notes recorded</p>
        </div>
        <div className="flex items-center gap-3">
          {['all','day','evening','night'].map(s => (
            <button key={s} onClick={() => setShiftFilter(s)} className={`px-4 py-2 rounded-2xl text-sm transition-all duration-200 ${shiftFilter === s ? 'bg-[rgb(var(--admin-accent))] text-white' : 'bg-[rgb(var(--admin-surface-2))] text-[rgb(var(--admin-text-primary))]'}`}>
              {s === 'all' ? 'All' : s === 'day' ? 'Day' : s === 'evening' ? 'Evening' : 'Night'}
            </button>
          ))}
          <button onClick={() => setShowForm(!showForm)} className="px-5 py-3 rounded-2xl bg-[rgb(var(--admin-accent))] text-white transition-all duration-200 hover:opacity-90 active:scale-[0.98] flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Note
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="rounded-3xl p-4 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm">
          <p className="text-sm text-[rgb(var(--admin-text-secondary))]">Notes Today</p>
          <p className="text-3xl font-bold text-[rgb(var(--admin-text-primary))]">{todayCount}</p>
        </div>
        <div className="rounded-3xl p-4 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm">
          <p className="text-sm text-[rgb(var(--admin-text-secondary))]">Day Shift</p>
          <p className="text-3xl font-bold text-[rgb(var(--admin-text-primary))]">{dayCount}</p>
        </div>
            <div className="rounded-3xl p-4 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm">
              <p className="text-sm text-[rgb(var(--admin-text-secondary))]">Evening Shift</p>
              <p className="text-3xl font-bold text-[rgb(var(--admin-text-primary))]">{eveningCount}</p>
            </div>
        <div className="rounded-3xl p-4 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm">
          <p className="text-sm text-[rgb(var(--admin-text-secondary))]">Night Shift</p>
          <p className="text-3xl font-bold text-[rgb(var(--admin-text-primary))]">{nightCount}</p>
        </div>
      </div>

      {/* Add Note Form */}
      {showForm && (
        <div className="rounded-3xl p-6 bg-[rgb(var(--admin-surface))] border-2 border-[rgb(var(--admin-accent))] shadow-warm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-[rgb(var(--admin-text-primary))]">New Handoff Note</h3>
            <button onClick={() => { setShowForm(false); setError(''); }} className="text-[rgb(var(--admin-text-muted))] hover:opacity-70"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm text-[rgb(var(--admin-text-secondary))] mb-1">Resident *</label>
              <select value={selectedResident} onChange={e => setSelectedResident(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-[rgb(var(--admin-border))] bg-[rgb(var(--admin-surface-2))] text-[rgb(var(--admin-text-primary))] outline-none">
                <option value="">Select resident...</option>
                {residents.map(r => <option key={r.id} value={r.id}>{r.full_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-[rgb(var(--admin-text-secondary))] mb-1">Shift</label>
              <select value={shift} onChange={e => setShift(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-[rgb(var(--admin-border))] bg-[rgb(var(--admin-surface-2))] text-[rgb(var(--admin-text-primary))] outline-none">
                <option value="day">Day</option>
                <option value="evening">Evening</option>
                <option value="night">Night</option>
              </select>
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-sm text-[rgb(var(--admin-text-secondary))] mb-1">Note *</label>
            <div className="flex items-center gap-2 mb-2">
              <button onClick={generateAiNote} disabled={generating || !selectedResident} className="px-3 py-1 text-xs rounded-xl bg-[rgb(var(--admin-accent))] text-white flex items-center gap-1 disabled:opacity-50"><Sparkles className="w-3 h-3" />{generating ? 'Generating...' : 'AI Draft'}</button>
            </div>
            <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="e.g. Margaret had a good day, ate well at lunch..." rows={4} className="w-full px-3 py-2 rounded-xl border border-[rgb(var(--admin-border))] bg-[rgb(var(--admin-surface-2))] text-[rgb(var(--admin-text-primary))] text-sm resize-y outline-none" />
          </div>
          {error && <div className="p-3 mb-4 rounded-xl bg-[rgb(var(--admin-danger)/0.1)] border border-[rgb(var(--admin-danger)/0.3)] text-[rgb(var(--admin-danger))] text-sm">{error}</div>}
          <div className="flex gap-3">
            <button onClick={saveNote} disabled={saving} className="px-5 py-2 rounded-2xl bg-[rgb(var(--admin-accent))] text-white font-medium transition-all duration-200 hover:opacity-90 active:scale-[0.98] disabled:opacity-70">{saving ? 'Saving...' : 'Save Note'}</button>
            <button onClick={() => { setShowForm(false); setError(''); }} className="px-4 py-2 rounded-2xl bg-[rgb(var(--admin-surface-2))] text-[rgb(var(--admin-text-muted))] font-medium transition-all duration-200 hover:opacity-80 active:scale-[0.98]">Cancel</button>
          </div>
        </div>
      )}

      {/* Notes — Grouped by Day */}
      {filtered.length === 0 ? (
        <div className="rounded-3xl p-12 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm text-center">
          <ClipboardList className="w-10 h-10 mx-auto mb-3 text-[rgb(var(--admin-text-muted))]" />
          <p className="text-[rgb(var(--admin-text-muted))]">No notes recorded yet</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([label, group]) => (
            <div key={label} className="space-y-4">
              <p className="text-sm font-medium text-[rgb(var(--admin-text-secondary))]">{label}</p>
              {group.map(note => (
                <div key={note.id} className="rounded-3xl p-5 bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm space-y-3 transition-all duration-200 hover:bg-[rgb(var(--admin-surface-2))]">

                  {/* Top Row: Identity + Meta + Shift Tag */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[rgb(var(--admin-surface-2))] flex items-center justify-center">
                        <User className="w-4 h-4 text-[rgb(var(--admin-text-muted))]" />
                      </div>
                      <div>
                        <div className="text-[rgb(var(--admin-text-primary))] font-medium">{note.resident_name}</div>
                        <div className="text-sm text-[rgb(var(--admin-text-muted))]">{note.room_number ? `Room ${note.room_number}` : ''}</div>
                      </div>
                    </div>
                    <span className="px-3 py-1 rounded-xl text-xs bg-[rgb(var(--admin-surface-2))] text-[rgb(var(--admin-text-secondary))]">
                      {note.shift === 'day' ? 'Day' : note.shift === 'night' ? 'Night' : 'Evening'}
                    </span>
                  </div>

                  {/* Meta line */}
                  <p className="text-sm text-[rgb(var(--admin-text-muted))]">{note.author_name} &bull; {formatTime(note.created_at)}</p>

                  {/* Note Type Label */}
                  <p className="text-xs text-[rgb(var(--admin-text-muted))]">{note.note_type}</p>

                  {/* Divider */}
                  <div className="border-t border-[rgb(var(--admin-border))]" />

                  {/* Content */}
                  <p className="text-sm leading-relaxed text-[rgb(var(--admin-text-secondary))] max-w-2xl">{note.note}</p>

                </div>
              ))}
            </div>
          ))}
        </div>
      )}

    </main>
  );
}